import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
/**
 * Where focus was when a modal `Dialog` / `AlertDialog` was opened — the
 * row's «⋮», the rail's «+», whatever the operator activated — so the
 * content can put it back on close.
 *
 * Radix's modal content handles close-focus itself: it `preventDefault`s the
 * FocusScope restore and focuses `context.triggerRef` — the `Dialog.Trigger`.
 * Almost every dialog in this app is opened from STATE and has no trigger,
 * so that ref is `null`, nothing is focused, and after «Отмена» focus sat on
 * `<body>` — the keyboard user lost their place in the table every time.
 *
 * The opener is captured in the RENDER in which a controlled `open` flips to
 * `true`, i.e. before anything of the dialog is committed. Nothing later is
 * reliable: `autoFocus` on the first field focuses during commit, before
 * `onOpenAutoFocus` and before any effect — a capture there records the
 * dialog's own field, which is gone by the time the dialog closes. An
 * uncontrolled dialog (with `defaultOpen` / a Radix `Trigger`) records
 * nothing and keeps Radix's default.
 */
const OpenerContext = React.createContext(null);
/** Wraps a Radix `Root` so its controlled `open` transition records the opener. */
export function withOpenerCapture(Root, displayName) {
    function CapturingRoot(props) {
        const openerRef = React.useRef(null);
        const wasOpen = React.useRef(false);
        const isOpen = props.open === true;
        if (isOpen && !wasOpen.current) {
            const active = typeof document === 'undefined' ? null : document.activeElement;
            openerRef.current = active instanceof HTMLElement ? active : null;
        }
        wasOpen.current = isOpen;
        return (_jsx(OpenerContext.Provider, { value: openerRef, children: _jsx(Root, { ...props }) }));
    }
    CapturingRoot.displayName = displayName;
    return CapturingRoot;
}
/**
 * `onCloseAutoFocus` for the content: returns focus to the recorded opener
 * while it is still in the document. A caller's own handler runs first and
 * wins if it `preventDefault`s; with no opener (uncontrolled dialog, or the
 * row was deleted) the event falls through to Radix's default.
 */
export function useRestoreFocusOnClose(onCloseAutoFocus) {
    const openerRef = React.useContext(OpenerContext);
    return React.useCallback((event) => {
        onCloseAutoFocus?.(event);
        if (event.defaultPrevented)
            return;
        const opener = openerRef?.current ?? null;
        if (opener && opener.isConnected) {
            event.preventDefault();
            opener.focus();
        }
    }, [onCloseAutoFocus, openerRef]);
}
//# sourceMappingURL=use-restore-focus-on-close.js.map